// Uebersicht der Serienmail-Laeufe im E-Mail-Bereich (Ordner „Serienmail“, Handoff E-Mails §8):
// Karten mit Bilanz und Fortschrittsbalken; Zahlen ausschliesslich aus baueBilanz.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleNotch, PaperPlaneRight, Plus, Users, WarningCircle } from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { baueBilanz, type EmpfaengerStatus } from '../../../lib/serienmail/fortschritt';
import { Chip, ImmoButton, LeerZustand } from '../../../components/immobilien/ui';
import { LAUF_STATUS, zielgruppenText, type LaufZielgruppe, bilanzText, geplantFuerText, type LaufStatus } from '../../../components/immobilien/email/serienmailAnzeige';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { Fortschrittsbalken } from './Fortschrittsbalken';

interface SerienmailZeile {
  id: string;
  betreff: string;
  status: LaufStatus;
  zielgruppe: LaufZielgruppe;
  zielgruppen?: string[] | null;
  created_at: string;
  geplant_am: string | null;
  erstellt_von: string | null;
  zeilen: { status: EmpfaengerStatus }[];
}

/** Pfade im E-Mail-Bereich (Entscheidung 1: /admin/serienmail → ?ordner=serienmail). */
export const SERIENMAIL_PFAD = '/admin/emails?ordner=serienmail';
export const laufPfad = (id: string) => `${SERIENMAIL_PFAD}&lauf=${encodeURIComponent(id)}`;

const SerienmailListe: React.FC = () => {
  const navigate = useNavigate();
  const { mobil } = useMobilModus();
  const [laeufe, setLaeufe] = useState<SerienmailZeile[]>([]);
  const [erstellerNamen, setErstellerNamen] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ladeLaeufe();
  }, []);

  const ladeLaeufe = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: ladeFehler } = await supabase
        .from('serienmails')
        .select('id, betreff, status, zielgruppe, zielgruppen, created_at, geplant_am, erstellt_von, zeilen:serienmail_empfaenger(status)')
        .order('created_at', { ascending: false });
      if (ladeFehler) throw ladeFehler;
      const geladen = (data || []) as SerienmailZeile[];
      setLaeufe(geladen);

      // Ersteller-Namen bewusst in einer eigenen Abfrage statt als profiles-Embed:
      // FK-Embeds auf profiles haben in diesem Projekt schon anderswo Listen zum
      // Leerlaufen gebracht, wenn PostgREST die Relation nicht eindeutig aufloesen konnte.
      const ids = Array.from(new Set(geladen.map((l) => l.erstellt_von).filter(Boolean))) as string[];
      if (ids.length > 0) {
        const { data: profile } = await supabase.from('profiles').select('id, full_name').in('id', ids);
        const zuordnung: Record<string, string> = {};
        (profile || []).forEach((p: { id: string; full_name: string | null }) => { zuordnung[p.id] = p.full_name || 'Unbekannt'; });
        setErstellerNamen(zuordnung);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden der Serienmail-Laeufe:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <div className="flex items-end justify-between flex-wrap" style={{ gap: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink2)' }}>Läufe an Mieter und Eigentümer</span>
        <ImmoButton art="akzent" groesse={44} icon={Plus} onClick={() => navigate(laufPfad('neu'))}>Neue Serienmail</ImmoButton>
      </div>

      {loading && laeufe.length === 0 ? (
        <div className="flex flex-col items-center justify-center" style={{ padding: '72px 0', gap: 12 }}>
          <CircleNotch size={28} className="animate-spin" color="var(--ink3)" />
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Lade Serienmail-Läufe...</p>
        </div>
      ) : error ? (
        <LeerZustand icon={WarningCircle} titel="Fehler beim Abrufen der Daten" text={error}>
          <ImmoButton groesse={44} onClick={ladeLaeufe} style={{ marginTop: 6 }}>Erneut versuchen</ImmoButton>
        </LeerZustand>
      ) : laeufe.length > 0 ? (
        <div className="flex flex-col" style={{ gap: 10 }}>
          {laeufe.map((lauf) => {
            const bilanz = baueBilanz(lauf.zeilen);
            const st = LAUF_STATUS[lauf.status];
            const geplant = lauf.status === 'geplant' ? geplantFuerText(lauf.geplant_am) : null;
            const meta = [
              zielgruppenText(lauf.zielgruppe, lauf.zielgruppen),
              new Date(lauf.created_at).toLocaleDateString('de-DE'),
              lauf.erstellt_von ? erstellerNamen[lauf.erstellt_von] || '…' : null,
            ].filter(Boolean).join(' · ');
            return (
              <div key={lauf.id} role="button" tabIndex={0}
                onClick={() => navigate(laufPfad(lauf.id))}
                onKeyDown={e => { if (e.key === 'Enter') navigate(laufPfad(lauf.id)); }}
                className="immo-glas immo-hover-kachel flex flex-col cursor-pointer min-w-0" style={{ borderRadius: 22, padding: 16, gap: 10 }}>
                <div className="flex items-start min-w-0" style={{ gap: 12 }}>
                  <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: 'var(--fill2)' }}>
                    <PaperPlaneRight size={18} weight="fill" color="var(--acc)" />
                  </span>
                  <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 800, overflowWrap: 'anywhere' }}>{lauf.betreff || '(ohne Betreff)'}</span>
                    <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{meta}</span>
                  </div>
                  <Chip ton={st.ton} groesse="s">{st.label}</Chip>
                </div>
                <div className="flex items-center flex-wrap" style={{ gap: mobil ? 6 : 14 }}>
                  <span className="flex-1" style={{ minWidth: 160 }}><Fortschrittsbalken bilanz={bilanz} /></span>
                  <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink2)', whiteSpace: 'nowrap' }}>{bilanzText(bilanz)}</span>
                </div>
                {geplant && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--info)' }}>{geplant}</span>}
              </div>
            );
          })}
          <span className="flex items-center" style={{ gap: 6, padding: '6px 4px', fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>
            <Users size={14} />{laeufe.length} Läufe insgesamt
          </span>
        </div>
      ) : (
        <LeerZustand icon={PaperPlaneRight} titel="Noch keine Serienmail-Läufe angelegt" />
      )}
    </div>
  );
};

export default SerienmailListe;
